/** Browser-safe timezone helpers used by the RSVP editor and its previews. */

export const timezones = [
  ...new Set(["UTC", ...(typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [])]),
];

export const commonTimezones: Record<string, string> = {
  UTC: "UTC GMT Universal Coordinated Universal Time",
  "America/New_York": "Eastern Time ET EST EDT New York US Canada",
  "America/Chicago": "Central Time CT CST CDT Chicago US Canada",
  "America/Denver": "Mountain Time MT MST MDT Denver US Canada",
  "America/Phoenix": "Arizona Mountain Standard Time MST Phoenix",
  "America/Los_Angeles": "Pacific Time PT PST PDT Los Angeles US Canada",
  "America/Anchorage": "Alaska Time AKST AKDT Anchorage",
  "Pacific/Honolulu": "Hawaii Time HST Honolulu",
  "America/Toronto": "Eastern Time ET EST EDT Toronto Canada",
  "America/Vancouver": "Pacific Time PT PST PDT Vancouver Canada",
  "America/Sao_Paulo": "Brasilia Time BRT Sao Paulo Brazil",
  "Europe/London": "United Kingdom UK London GMT BST British Time",
  "Europe/Dublin": "Ireland Dublin GMT IST Irish Time",
  "Europe/Berlin": "Central European Time CET CEST Germany Berlin",
  "Europe/Paris": "Central European Time CET CEST France Paris",
  "Europe/Amsterdam": "Central European Time CET CEST Netherlands Amsterdam",
  "Europe/Madrid": "Central European Time CET CEST Spain Madrid",
  "Europe/Rome": "Central European Time CET CEST Italy Rome",
  "Europe/Athens": "Eastern European Time EET EEST Greece Athens",
  "Europe/Helsinki": "Eastern European Time EET EEST Finland Helsinki",
  "Europe/Warsaw": "Central European Time CET CEST Poland Warsaw",
  "Europe/Istanbul": "Turkey Time TRT Istanbul",
  "Asia/Dubai": "Gulf Standard Time GST Dubai UAE",
  "Asia/Kolkata": "India Standard Time IST India Delhi Mumbai Kolkata",
  "Asia/Singapore": "Singapore Time SGT",
  "Asia/Hong_Kong": "Hong Kong Time HKT",
  "Asia/Tokyo": "Japan Standard Time JST Tokyo",
  "Asia/Seoul": "Korea Standard Time KST Seoul",
  "Asia/Shanghai": "China Standard Time CST Beijing Shanghai",
  "Australia/Perth": "Australian Western Time AWST Perth",
  "Australia/Adelaide": "Australian Central Time ACST ACDT Adelaide",
  "Australia/Sydney": "Australian Eastern Time AEST AEDT Sydney",
  "Pacific/Auckland": "New Zealand Time NZST NZDT Auckland",
};

const timezoneDisplayNames: Record<string, string> = {
  UTC: "Coordinated Universal Time",
  "America/New_York": "Eastern Time (New York)",
  "America/Chicago": "Central Time (Chicago)",
  "America/Denver": "Mountain Time (Denver)",
  "America/Phoenix": "Arizona Time (Phoenix)",
  "America/Los_Angeles": "Pacific Time (Los Angeles)",
  "America/Anchorage": "Alaska Time (Anchorage)",
  "Pacific/Honolulu": "Hawaii Time (Honolulu)",
  "America/Toronto": "Eastern Time (Toronto)",
  "America/Vancouver": "Pacific Time (Vancouver)",
  "Europe/London": "United Kingdom Time (London)",
  "Europe/Berlin": "Central European Time (Berlin)",
  "Europe/Paris": "Central European Time (Paris)",
  "Europe/Amsterdam": "Central European Time (Amsterdam)",
  "Europe/Athens": "Eastern European Time (Athens)",
  "Asia/Dubai": "Gulf Time (Dubai)",
  "Asia/Kolkata": "India Standard Time (Kolkata)",
  "Asia/Singapore": "Singapore Time",
  "Asia/Tokyo": "Japan Standard Time (Tokyo)",
  "Asia/Seoul": "Korea Standard Time (Seoul)",
  "Asia/Shanghai": "China Standard Time (Shanghai)",
  "Australia/Perth": "Australian Western Time (Perth)",
  "Australia/Adelaide": "Australian Central Time (Adelaide)",
  "Australia/Sydney": "Australian Eastern Time (Sydney)",
  "Pacific/Auckland": "New Zealand Time (Auckland)",
};

export function zonedInputValue(iso: string, timezone: string) {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "";
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
        .formatToParts(date)
        .map((part) => [part.type, part.value]),
    );
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch {
    return iso.slice(0, 16);
  }
}

export function zonedInputToIso(value: string, timezone: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return new Date().toISOString();
  const [, year, month, day, hour, minute] = match;
  const desired = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
  let instant = desired;
  try {
    for (let pass = 0; pass < 3; pass += 1) {
      const rendered = Object.fromEntries(
        new Intl.DateTimeFormat("en-CA", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(new Date(instant))
          .map((part) => [part.type, part.value]),
      );
      const renderedUtc = Date.UTC(
        Number(rendered.year),
        Number(rendered.month) - 1,
        Number(rendered.day),
        Number(rendered.hour),
        Number(rendered.minute),
      );
      instant += desired - renderedUtc;
    }
  } catch {
    return new Date(desired).toISOString();
  }
  return new Date(instant).toISOString();
}

export function timezoneOffsetLabel(timezone: string, date = new Date()) {
  try {
    return (
      new Intl.DateTimeFormat("en", { timeZone: timezone, timeZoneName: "shortOffset" })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")
        ?.value.replace("GMT", "UTC") ?? "UTC"
    );
  } catch {
    return "UTC";
  }
}

export function timezoneShortName(timezone: string, date = new Date()) {
  try {
    return (
      new Intl.DateTimeFormat("en", { timeZone: timezone, timeZoneName: "short" })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    return "";
  }
}

export function timezonePlace(timezone: string) {
  if (timezoneDisplayNames[timezone]) return timezoneDisplayNames[timezone];
  const [region, ...place] = timezone.split("/");
  return `${place.join(" / ").replaceAll("_", " ")} · ${region}`;
}

export function timezoneSearchText(timezone: string) {
  return [
    timezone,
    timezone.replaceAll("_", " "),
    timezonePlace(timezone),
    timezoneOffsetLabel(timezone),
    timezoneShortName(timezone),
    commonTimezones[timezone] ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

export function timezoneIsSupported(timezone: string) {
  try {
    new Intl.DateTimeFormat("en", { timeZone: timezone }).format();
    return true;
  } catch {
    return false;
  }
}
